import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import api from "../../api";
import { SCAN_UPLOAD_STAGES } from "./constants";
import useScanDropZone from "./useScanDropZone";

const POLL_INTERVAL_MS = 3000;
const POLL_ERROR_INTERVAL_MS = 5000;
const MAX_POLLS = 200; // ~10 minutes

/**
 * Upload a photo/scan of a handwritten paper and watch it being marked.
 *
 * `endpoints` supplies the module-specific URLs so the same dialog serves past papers and
 * mock exams: { validate, upload, status: (submissionId) => url }.
 */
export default function ScanUploadDialog({ open, onClose, title, endpoints, onComplete }) {
  const { file, dropZoneActive, dropZoneProps, handleFileChange, reset } = useScanDropZone();

  const [validating, setValidating] = useState(false);
  const [validationWarning, setValidationWarning] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stage, setStage] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const pollTimerRef = useRef(null);
  const pollCountRef = useRef(0);
  const closedRef = useRef(false);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPoll(), [clearPoll]);

  useEffect(() => {
    if (open) {
      closedRef.current = false;
      pollCountRef.current = 0;
      return;
    }
    closedRef.current = true;
    clearPoll();
    reset();
    setValidating(false);
    setValidationWarning(null);
    setUploading(false);
    setUploadProgress(0);
    setStage(null);
    setResult(null);
    setError(null);
  }, [open, clearPoll, reset]);

  const pollStatus = useCallback(
    (submissionId) => {
      if (closedRef.current) return;
      api
        .get(endpoints.status(submissionId))
        .then((res) => {
          if (closedRef.current) return;
          const data = res.data?.data ?? {};

          if (data.status === "completed") {
            clearPoll();
            setStage("complete");
            setUploading(false);
            setResult(data);
            toast.success("Your paper has been marked.");
            return;
          }
          if (data.status === "failed") {
            clearPoll();
            setStage(null);
            setUploading(false);
            setError(data.error || "We couldn't mark this scan.");
            return;
          }

          setStage(data.stage === "grading" ? "grading" : "processing");
          pollCountRef.current += 1;
          if (pollCountRef.current >= MAX_POLLS) {
            clearPoll();
            setUploading(false);
            setError("Marking is taking longer than expected. Check your results in a few minutes.");
            return;
          }
          pollTimerRef.current = setTimeout(() => pollStatus(submissionId), POLL_INTERVAL_MS);
        })
        .catch(() => {
          if (closedRef.current) return;
          pollCountRef.current += 1;
          if (pollCountRef.current >= MAX_POLLS) {
            clearPoll();
            setUploading(false);
            setError("Lost contact while marking. Check your results in a few minutes.");
            return;
          }
          pollTimerRef.current = setTimeout(() => pollStatus(submissionId), POLL_ERROR_INTERVAL_MS);
        });
    },
    [endpoints, clearPoll]
  );

  const doUpload = useCallback(async () => {
    const form = new FormData();
    form.append("scan", file);

    setUploading(true);
    setError(null);
    setUploadProgress(0);
    setStage("uploading");

    try {
      const res = await api.post(endpoints.upload, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const percent = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(percent);
          if (percent === 100) setStage("processing");
        },
      });
      const submissionId = res.data?.data?.submission_id;
      if (!submissionId) {
        throw new Error("Upload did not return a submission id.");
      }
      pollCountRef.current = 0;
      pollStatus(submissionId);
    } catch (e) {
      setUploading(false);
      setStage(null);
      setError(e.response?.data?.message || e.message || "Upload failed.");
    }
  }, [file, endpoints, pollStatus]);

  /** Confirm the scan is this paper before spending the marking pass on it. Fails open. */
  const handleStart = useCallback(async () => {
    if (!file) return;
    setValidationWarning(null);

    if (endpoints.validate) {
      const form = new FormData();
      form.append("scan", file);
      setValidating(true);
      try {
        const res = await api.post(endpoints.validate, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const data = res.data?.data;
        if (data && data.method !== "skipped" && !data.valid) {
          setValidationWarning({ issues: data.issues || [], paperName: data.paper_name || title });
          setValidating(false);
          return;
        }
      } catch (_) {
        // Never block the student on a validation outage.
      } finally {
        setValidating(false);
      }
    }

    doUpload();
  }, [file, endpoints, title, doUpload]);

  const busy = validating || uploading;
  const activeStep = SCAN_UPLOAD_STAGES.findIndex((s) => s.key === stage);
  const skipped = result?.skipped_questions ?? [];

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        Upload your written paper
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.25 }}>
          {title}
        </Typography>
        {!busy && (
          <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {!result && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Photograph or scan every page you wrote on, then upload it here. We&apos;ll read your
              answers and mark them against this paper.
            </Typography>

            <Box
              component="label"
              htmlFor="scan-file-input"
              {...dropZoneProps}
              sx={{
                display: "block",
                mb: 2,
                borderRadius: 2,
                border: "2px dashed",
                borderColor: dropZoneActive ? "primary.main" : "divider",
                bgcolor: dropZoneActive ? "action.hover" : "background.paper",
                py: 3,
                px: 2,
                cursor: busy ? "default" : "pointer",
                textAlign: "center",
                transition: "border-color 0.2s ease, background-color 0.2s ease",
                "&:focus-within": { borderColor: "primary.light", bgcolor: "action.hover" },
              }}
            >
              <input
                id="scan-file-input"
                type="file"
                hidden
                accept=".pdf,.png,.jpg,.jpeg"
                disabled={busy}
                onChange={handleFileChange}
              />
              <Stack alignItems="center" spacing={1}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: file ? 600 : 500, color: "text.primary", wordBreak: "break-word" }}
                >
                  {file ? file.name : "Drag & drop a file here, or click to browse"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF, PNG, or JPG — up to 20 MB
                </Typography>
              </Stack>
            </Box>

            {validating && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mt: 2,
                  p: 1.5,
                  bgcolor: "rgba(102,126,234,0.07)",
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                  Checking this is the right paper…
                </Typography>
              </Box>
            )}

            {validationWarning && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>This may not be the right paper</AlertTitle>
                {validationWarning.issues.length > 0 ? (
                  <ul style={{ margin: "4px 0 8px", paddingLeft: 18 }}>
                    {validationWarning.issues.map((issue, i) => (
                      <li key={i}>
                        <Typography variant="body2">{issue}</Typography>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    We couldn&apos;t confirm this scan matches <strong>{validationWarning.paperName}</strong>.
                  </Typography>
                )}
                <Button size="small" variant="contained" color="warning" onClick={doUpload}>
                  Mark it anyway
                </Button>
              </Alert>
            )}

            {uploading && stage !== "complete" && (
              <Box sx={{ mt: 2 }}>
                <Stepper activeStep={activeStep < 0 ? 0 : activeStep} alternativeLabel>
                  {SCAN_UPLOAD_STAGES.map((s) => (
                    <Step key={s.key}>
                      <StepLabel>{s.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                {stage === "uploading" ? (
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">
                      {stage === "grading" ? "Marking your answers…" : "Reading your handwriting…"}
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                  This can take a couple of minutes. Keep this window open.
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {result && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 32 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Marked
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Number(result.score ?? 0).toFixed(1)} / {Number(result.total_marks ?? 0).toFixed(0)} marks
                  {result.percentage != null && ` · ${result.percentage}%`}
                </Typography>
              </Box>
            </Box>

            {result.markable_marks != null && result.total_marks != null
              && Number(result.markable_marks) < Number(result.total_marks) && (
              <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  {Number(result.markable_marks).toFixed(0)} of {Number(result.total_marks).toFixed(0)} marks
                  could be marked from your scan
                </AlertTitle>
                <Typography variant="body2">
                  The rest count as 0 in your score. Answer them online to be marked on those.
                </Typography>
              </Alert>
            )}

            {skipped.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Not marked from this scan ({skipped.length})
                </Typography>
                <Stack spacing={0.5} sx={{ maxHeight: 180, overflowY: "auto" }}>
                  {skipped.map((s, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Chip label={s.question} size="small" sx={{ fontWeight: 700, height: 20, fontSize: "0.68rem" }} />
                      <Typography variant="caption" color="text.secondary">
                        {s.reason}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {result.low_confidence?.length > 0 && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                We were less sure reading {result.low_confidence.length} answer
                {result.low_confidence.length === 1 ? "" : "s"} ({result.low_confidence.slice(0, 8).join(", ")}
                {result.low_confidence.length > 8 ? "…" : ""}). Check these in your report.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        {result ? (
          <>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => onComplete?.(result)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              View report
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!file || busy}
              onClick={handleStart}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {uploading ? "Marking…" : validating ? "Checking…" : "Upload & mark"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
